import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type TeamFormSchema, teamFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { TeamForm } from "@/forms/team-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditTeamModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	teamId: string;
}

export function EditTeamModal({ onClose, isOpen, onComplete, teamId }: EditTeamModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.organizations.teams[":teamId"].$get({
			param: { teamId },
		});
		const data = await responseHandler(response);
		return data.team;
	});
	const methods = useForm({
		resolver: resolver(teamFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: TeamFormSchema) {
		const response = await client.organizations.teams[":teamId"].$patch({
			param: { teamId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("organization.edit_team")}
				isOpen={isOpen}
				onClose={onClose}
				isLoading={methods.formState.isLoading && !loadFailed}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<TeamForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
